import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Users, MessageSquare, Gift, Music, Phone, MapPin, Calendar, Heart,
  ChevronDown, ChevronUp, Play, CheckCircle2, Sparkles, ArrowRight, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback sample cards if no preview cards available
const sampleCards = [
  {
    id: 1,
    title: "Floral Elegance",
    category: "Floral",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=600&fit=crop",
    slug: "floral-elegance"
  },
  {
    id: 2,
    title: "Modern Minimalist",
    category: "Moden",
    image: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=600&fit=crop",
    slug: "modern-minimalist"
  },
  {
    id: 3,
    title: "Traditional Gold",
    category: "Tradisional",
    image: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=600&fit=crop",
    slug: "traditional-gold"
  },
  {
    id: 4,
    title: "Rustic Garden",
    category: "Rustic",
    image: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=400&h=600&fit=crop",
    slug: "rustic-garden"
  }
];

const features = [
  {
    icon: Users,
    title: "RSVP",
    description: "Tetamu boleh menghantar status kehadiran mereka kepada pihak majlis"
  },
  {
    icon: Heart,
    title: "Money Gift",
    description: "Terima wang tanpa tunai terus ke akaun bank anda dengan QR code"
  },
  {
    icon: Gift,
    title: "Wishlist",
    description: "Terima hadiah idaman anda tanpa sebarang pertindihan"
  },
  {
    icon: Music,
    title: "Muzik",
    description: "Pelbagai pilihan lagu untuk muzik latar belakang kad anda"
  },
  {
    icon: Phone,
    title: "Hubungi",
    description: "Hubungi tuan rumah melalui telefon atau WhatsApp dengan satu klik"
  },
  {
    icon: MapPin,
    title: "Lokasi",
    description: "Penunjuk arah menggunakan aplikasi Google Maps atau Waze"
  },
  {
    icon: Calendar,
    title: "Kalendar",
    description: "Simpan tarikh majlis sebagai peringatan di aplikasi kalendar"
  },
  {
    icon: MessageSquare,
    title: "Guestbook",
    description: "Ruangan untuk tetamu menyampaikan ucapan dan doa"
  }
];

const faqs = [
  {
    question: "Apakah itu Kad Kahwin Digital?",
    answer: "Kad kahwin digital ialah versi elektronik kad jemputan kahwin yang tradisional. Ianya boleh dihantar melalui WhatsApp, Telegram atau dikongsi melalui platform media sosial. Kad jemputan digital menjadi semakin popular kerana memudahkan pihak majlis dan tetamu, menjimatkan kos dan lebih mesra alam."
  },
  {
    question: "Apakah fungsi yang terdapat pada kad digital?",
    answer: "Kad digital Kawkad dilengkapi dengan fungsi RSVP, Money Gift, Wishlist, Muzik latar, Hubungi, Lokasi dengan Google Maps/Waze, Kalendar, Guestbook, Countdown, dan Galeri gambar."
  },
  {
    question: "Bagaimanakah cara menempah kad digital?",
    answer: "Untuk menempah kad digital, anda perlu daftar akaun dan log masuk. Kemudian, pilih design kad digital dan isikan maklumat majlis pada borang yang disediakan dengan lengkap. Setelah selesai, kad anda sedia untuk dikongsi!"
  },
  {
    question: "Bolehkah maklumat ditukar setelah kad digital siap?",
    answer: "Ya! Sebarang pertukaran maklumat boleh dibuat pada bila-bila masa sebelum atau selepas tarikh majlis. Anda mempunyai kawalan penuh terhadap kad anda."
  },
  {
    question: "Adakah kad digital mempunyai muzik latar belakang?",
    answer: "Ya, kad digital ini mempunyai muzik latar belakang. Anda boleh pilih dari galeri lagu kami atau masukkan link audio sendiri."
  },
  {
    question: "Perlukah download di Appstore atau Playstore?",
    answer: "Tidak perlu. Kawkad adalah sebuah web-based app yang boleh diakses menggunakan browser di mana-mana peranti."
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [previewCards, setPreviewCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [videoThumbnail, setVideoThumbnail] = useState('');
  const [seoSettings, setSeoSettings] = useState({
    seo_title: 'Kawkad - Kad Kahwin Digital Malaysia',
    seo_description: 'Cipta kad kahwin digital yang cantik dan interaktif. Murah, pantas dan mampu milik.',
    seo_keywords: 'kad kahwin digital, e-kad kahwin, kad jemputan digital, malaysia',
    seo_og_image: 'https://customer-assets.emergentagent.com/job_kawkad-invites/artifacts/6rrukilt_kawkad.jpeg'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch preview cards and landing settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardsRes, catsRes, landingRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/preview-cards`),
          axios.get(`${BACKEND_URL}/api/preview-categories`),
          axios.get(`${BACKEND_URL}/api/landing-settings`)
        ]);
        setPreviewCards(cardsRes.data?.slice(0, 4) || []);
        setCategories(catsRes.data || []);
        const landingData = landingRes.data || {};
        setYoutubeVideoId(landingData.youtube_video_id || '');
        setVideoThumbnail(landingData.video_thumbnail || '');
        setSeoSettings({
          seo_title: landingData.seo_title || 'Kawkad - Kad Kahwin Digital Malaysia',
          seo_description: landingData.seo_description || 'Cipta kad kahwin digital yang cantik dan interaktif.',
          seo_keywords: landingData.seo_keywords || 'kad kahwin digital, e-kad kahwin',
          seo_og_image: landingData.seo_og_image || ''
        });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Get category name helper
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat?.name || '';
  };

  // Display cards - use preview cards if available, else fallback to sample
  const displayCards = previewCards.length > 0 ? previewCards : sampleCards;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoSettings.seo_title}</title>
        <meta name="description" content={seoSettings.seo_description} />
        <meta name="keywords" content={seoSettings.seo_keywords} />
        <meta property="og:title" content={seoSettings.seo_title} />
        <meta property="og:description" content={seoSettings.seo_description} />
        {seoSettings.seo_og_image && <meta property="og:image" content={seoSettings.seo_og_image} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoSettings.seo_title} />
        <meta name="twitter:description" content={seoSettings.seo_description} />
      </Helmet>

      {/* Navigation - Glassmorphism style */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className={`text-2xl font-bold transition-colors ${scrolled ? 'text-[#3D348B]' : 'text-white'}`}>
            Kawkad
          </h1>
          <div className="flex gap-3">
            <Button
              data-testid="nav-login-btn"
              variant="ghost"
              onClick={() => navigate('/login')}
              className={`transition-colors ${scrolled ? 'text-[#3D348B] hover:bg-[#7678ED]/10 hover:text-[#3D348B]' : 'text-white hover:bg-white/10 hover:text-white'}`}
            >
              Log Masuk
            </Button>
            <Button
              data-testid="nav-register-btn"
              onClick={() => navigate('/register')}
              className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full px-6 font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Daftar Sekarang
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Modern gradient with animated elements */}
      <section className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden" style={{
        background: 'linear-gradient(135deg, #3D348B 0%, #7678ED 50%, #3D348B 100%)'
      }}>
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#F7B801]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#F18701]/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#7678ED]/30 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-6xl mx-auto relative z-10 pt-20 pb-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="text-white space-y-8">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm">
                <Sparkles className="w-4 h-4 text-[#F7B801]" />
                <span>Kad Kahwin Digital #1 Malaysia</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Kad Kahwin Digital
                <span className="block text-[#F7B801] mt-2">Murah & Canggih</span>
              </h1>
              
              <p className="text-lg text-white/80 max-w-xl">
                Dilengkapi dengan RSVP, Guestbook, Muzik, Galeri Foto, Money Gift, Wishlist, Navigasi, dan Kalendar. Sempurna untuk majlis perkahwinan anda.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/register')}
                  className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full px-8 py-6 text-lg font-bold shadow-2xl hover:shadow-[#F7B801]/25 hover:scale-105 transition-all"
                >
                  Mula Sekarang
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  data-testid="hero-view-samples-btn"
                  variant="outline"
                  onClick={() => document.getElementById('contoh-kad').scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-white/50 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                >
                  Lihat Contoh
                </Button>
              </div>
            </div>

            {/* Right - Video placeholder with modern frame */}
            <div className="relative">
              <div 
                className="relative aspect-video bg-black/20 rounded-3xl overflow-hidden shadow-2xl cursor-pointer group backdrop-blur-sm border border-white/20"
                onClick={() => setIsVideoPlaying(true)}
                data-testid="video-placeholder"
              >
                {!isVideoPlaying ? (
                  <>
                    <img 
                      src={videoThumbnail || "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&h=675&fit=crop"}
                      alt="Video thumbnail"
                      className="absolute inset-0 w-full h-full object-cover opacity-70"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D348B]/80 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="w-20 h-20 bg-[#F7B801] rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-[#3D348B] ml-1" fill="currentColor" />
                      </div>
                      <p className="mt-4 text-white font-medium text-lg">Tonton Video Demo</p>
                    </div>
                  </>
                ) : (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
                    title="Kawkad Demo Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
              <p className="text-white/50 text-sm mt-3 text-center">* Video demo akan ditambah</p>
            </div>
          </div>

          {/* Stats - Centered below hero content */}
          <div className="mt-16 flex justify-center">
            <div className="inline-flex gap-12 sm:gap-16 bg-white/10 backdrop-blur-sm rounded-2xl px-10 py-6 border border-white/20">
              <div className="text-center" data-testid="stat-orders">
                <p className="text-3xl sm:text-4xl font-bold text-[#F7B801]">100+</p>
                <p className="text-white/70 text-sm mt-1">Tempahan</p>
              </div>
              <div className="text-center" data-testid="stat-designs">
                <p className="text-3xl sm:text-4xl font-bold text-[#F7B801]">50+</p>
                <p className="text-white/70 text-sm mt-1">Design</p>
              </div>
              <div className="text-center" data-testid="stat-support">
                <p className="text-3xl sm:text-4xl font-bold text-[#F7B801]">24/7</p>
                <p className="text-white/70 text-sm mt-1">Dibuka</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Sample Cards Section */}
      <section id="contoh-kad" className="py-24 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#7678ED]/10 text-[#3D348B] rounded-full px-4 py-2 text-sm font-medium mb-4">
              Koleksi Design
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D348B] mb-4">
              Pelbagai Pilihan Design
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Pilih design kad kahwin digital yang menepati dengan cita rasa anda
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {displayCards.map((card, index) => {
              const isRealCard = card.card_id; // Real preview cards have card_id
              return (
                <div 
                  key={card.card_id || card.id}
                  onClick={() => isRealCard ? window.open(`/${card.slug}`, '_blank') : navigate('/preview-kad')}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                  data-testid={`sample-card-${index + 1}`}
                >
                  {/* Phone frame mockup */}
                  <div className="relative aspect-[9/16] bg-gray-100">
                    <img 
                      src={isRealCard ? (card.preview_thumbnail || card.background_thumbnail || card.background_image) : card.image}
                      alt={isRealCard ? `${card.bride_name} & ${card.groom_name}` : card.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D348B] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Button
                        size="sm"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (isRealCard) {
                            window.open(`/${card.slug}`, '_blank');
                          } else {
                            navigate('/preview-kad');
                          }
                        }}
                        className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] font-semibold"
                      >
                        {isRealCard ? (
                          <>
                            <ExternalLink className="w-4 h-4 mr-1" /> Lihat Kad
                          </>
                        ) : 'Lihat Preview'}
                      </Button>
                    </div>
                    {/* Badge - LIVE for real cards, POPULAR for samples */}
                    <div className={`absolute top-3 left-3 text-white text-xs font-bold px-3 py-1 rounded-full ${
                      isRealCard ? 'bg-green-500' : 'bg-[#F35B04]'
                    }`}>
                      {isRealCard ? 'LIVE' : 'POPULAR'}
                    </div>
                    {/* Category badge */}
                    {isRealCard && card.preview_category && (
                      <div className="absolute bottom-3 left-3 bg-white/90 text-[#3D348B] text-xs px-2 py-1 rounded-full">
                        {getCategoryName(card.preview_category)}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-[#3D348B]">
                      {isRealCard ? `${card.bride_name} & ${card.groom_name}` : card.title}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {isRealCard ? card.wedding_date : card.category}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/preview-kad')}
              className="bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="view-all-designs-btn"
            >
              Lihat Semua Design
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section - Bento Grid Style */}
      <section id="features" className="py-24 px-4 bg-[#3D348B]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-white/10 text-[#F7B801] rounded-full px-4 py-2 text-sm font-medium mb-4">
              Fungsi Lengkap
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              8 Fungsi Kad Kahwin Digital
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Kad kahwin digital kami dilengkapi dengan pelbagai fungsi yang menarik
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-[#F7B801]/50 transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#F7B801] to-[#F18701] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-[#3D348B]" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Modern cards */}
      <section className="py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#F7B801]/10 text-[#F18701] rounded-full px-4 py-2 text-sm font-medium mb-4">
              Kelebihan
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D348B] mb-4">
              Kenapa Pilih Kawkad?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Design cantik dan moden",
              "Mudah digunakan (user-friendly)",
              "Boleh edit bila-bila masa",
              "Kongsi melalui WhatsApp & media sosial",
              "Jimat kos berbanding kad fizikal",
              "Mesra alam - tanpa pembaziran kertas",
              "Sokongan pelanggan 24/7",
              "Pelbagai pilihan muzik latar"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <div className="w-10 h-10 bg-gradient-to-br from-[#F7B801] to-[#F18701] rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#7678ED]/10 text-[#3D348B] rounded-full px-4 py-2 text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D348B] mb-4">
              Soalan Lazim
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-2xl overflow-hidden hover:border-[#7678ED] transition-colors"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-[#3D348B] pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    openFaq === index ? 'bg-[#3D348B] text-white' : 'bg-gray-100 text-[#3D348B]'
                  }`}>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Vibrant gradient */}
      <section className="py-24 px-4 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #F7B801 0%, #F18701 50%, #F35B04 100%)'
      }}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#3D348B]/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D348B] mb-6">
            Mulakan Perjalanan Perkahwinan Anda
          </h2>
          <p className="text-lg text-[#3D348B]/80 mb-8 max-w-2xl mx-auto">
            Cipta kad jemputan digital yang sempurna dalam beberapa minit sahaja. Percuma untuk bermula!
          </p>
          <Button
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')}
            className="bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full px-12 py-6 text-lg font-bold shadow-2xl hover:shadow-[#3D348B]/25 hover:scale-105 transition-all"
          >
            Cipta Kad Sekarang - Percuma!
            <Sparkles className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#3D348B]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Kawkad</h3>
              <p className="text-white/60">Kad Kahwin Digital Pilihan Rakyat Malaysia</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Pautan</h4>
              <div className="flex flex-col gap-2">
                <a href="#features" className="text-white/60 hover:text-[#F7B801] transition-colors">Fungsi</a>
                <a href="#contoh-kad" className="text-white/60 hover:text-[#F7B801] transition-colors">Contoh Kad</a>
                <button onClick={() => navigate('/login')} className="text-left text-white/60 hover:text-[#F7B801] transition-colors">Log Masuk</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Hubungi</h4>
              <p className="text-white/60">hello@kawkad.com</p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/40 text-sm">© 2025 Kawkad. Semua hak terpelihara.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
